/* cwclientlibjs
   copyright 2015 LOGILAB S.A. (Paris, FRANCE), all rights reserved.
   contact http://www.logilab.fr/ -- mailto:contact@logilab.fr
*/

import {RqlClient, RqlRow, RqlQuery} from './client';

/**
 * A complete schema
 */
export interface Schema {
    /**
     * The various metadata for this schema
     */
    metadata: {[key: string]: any};
    /**
     * The schemas for the entities
     */
    entities: EntitySchema[];
}

/**
 * The schema of an entity
 */
export interface EntitySchema {
    /**
     * The entity schema's EID
     */
    eid: number;
    /**
     * The entity's name
     */
    name: string;
    /**
     * The last modification date of the entity schema
     */
    modificationDate: string;
    /**
     * The EID of the specialized entity type, if any
     */
    specializes: number | null;
    /**
     * The name of its attributes in order
     */
    attributes: EntityAttributeSchema[];
    /**
     * The relations for which this entity is an object
     */
    relationsFrom: EntityRelationSchema[];
    /**
     * The relations for which this entity is a subject
     */
    relationsTo: EntityRelationSchema[];
}

/**
 * The cardinality for an end in a relation
 */
export enum EntityRelationSchemaCardinality {
    /**
     * Unknown cardinality
     */
    Unknown,
    /**
     * Zero or more (*)
     */
    ZeroOrMore,
    /**
     * Zero or one (optional) (?)
     */
    ZeroOrOne,
    /**
     * Exactly one (1)
     */
    ExactlyOne,
    /**
     * One or more (+)
     */
    OneOrMore,
}

/**
 * Gets the cardinality for its string representation
 * @param c The string representation
 */
export function getCardinality(c: string): EntityRelationSchemaCardinality {
    if (c === '*') {
        return EntityRelationSchemaCardinality.ZeroOrMore;
    } else if (c === '?') {
        return EntityRelationSchemaCardinality.ZeroOrOne;
    } else if (c === '1') {
        return EntityRelationSchemaCardinality.ExactlyOne;
    } else if (c === '+') {
        return EntityRelationSchemaCardinality.OneOrMore;
    } else {
        return EntityRelationSchemaCardinality.Unknown;
    }
}

/**
 * Gets the cardinality for the 'from' end of a relation
 * @param cardString The cardinality string for the relation
 */
export function getFromCardinality(
    cardString: string | undefined | null
): EntityRelationSchemaCardinality {
    if (
        cardString === null ||
        cardString === undefined ||
        cardString.length <= 1
    ) {
        return EntityRelationSchemaCardinality.Unknown;
    }
    return getCardinality(cardString[1]);
}

/**
 * Gets the cardinality for the 'to' end of a relation
 * @param cardString The cardinality string for the relation
 */
export function getToCardinality(
    cardString: string | undefined | null
): EntityRelationSchemaCardinality {
    if (
        cardString === null ||
        cardString === undefined ||
        cardString.length === 0
    ) {
        return EntityRelationSchemaCardinality.Unknown;
    }
    return getCardinality(cardString[0]);
}

/**
 * The schema of an attribute
 */
export interface EntityAttributeSchema {
    /**
     * The attribute's EID
     */
    eid: number;
    /**
     * The attribute's name
     */
    name: string;
    /**
     * The attribute's type
     */
    type: string;
    /**
     * The cardinality for the attribute
     */
    cardinality: EntityRelationSchemaCardinality;
}

/**
 * The schema of a relation between entities
 */
export interface EntityRelationSchema {
    /**
     * EID for the relation
     */
    eid: number;
    /**
     * The EID of the subject entity type
     */
    from: number;
    /**
     * The cardinality on the 'from' end of the relation
     */
    fromCardinality: EntityRelationSchemaCardinality;
    /**
     * The name of the subject entity type
     */
    fromName: string;
    /**
     * The EID of the object entity type
     */
    to: number;
    /**
     * The cardinality on the 'to' end of the relation
     */
    toCardinality: EntityRelationSchemaCardinality;
    /**
     * The name of the object entity type
     */
    toName: string;
    /**
     * The relation's name
     */
    name: string;
    /**
     * Human-readable description for this relation
     */
    description: string;
}

/**
 * Gets the unique name for an entity relation
 * @param relation The relation
 */
export function getUniqueRelationName(relation: EntityRelationSchema): string {
    return `${relation.name}_${relation.fromName}_${relation.toName}`;
}

/**
 * A provider of entity schemas
 */
export interface EntitySchemaProvider {
    /**
     * Gets all relations (included inherited ones) for an entity type
     * @param typeEid The EID of the entity type
     */
    getAllRelationsFor(typeEid: number): {
        relationsTo: EntityRelationSchema[];
        relationsFrom: EntityRelationSchema[];
    };
    /**
     * Gets an entity schema for the entity type with the specified name
     * @param typeName The name of an entity type
     */
    getEntitySchemaByName(typeName: string): Promise<EntitySchema | null>;
    /**
     * Gets an entity schema for the entity type with the specified eid
     * @param typeEid The eid of an entity type
     */
    getEntitySchemaById(typeEid: number): Promise<EntitySchema | null>;
    /**
     * Gets the entity schema for the corresponding entity (instance)
     * @param eid The eid of an entity
     */
    getEntitySchemaFor(eid: number): Promise<EntitySchema | null>;
}

/**
 * Maps a possibly undefined value to a nullable value
 * @param x The value to map
 */
function undefinedToNull<X>(x: X | undefined): X | null {
    if (x === undefined) {
        return null;
    } else {
        return x;
    }
}

/**
 * A component that is able to load entity schemas from somewhere
 */
export interface EntitySchemasLoader {
    load(): Promise<Schema>;
    getSchemaHash(): Promise<string | null>;
}

/**
 * A loader that looks for the schemas in the browser's local storage before delegating to an innner loader
 */
export class LocalStorageEntitySchemasLoader implements EntitySchemasLoader {
    /**
     * The inner loader
     */
    private readonly inner: EntitySchemasLoader;
    /**
     * The key to the storage to use
     */
    private readonly storageKey: string;
    /**
     * The key to the storage for the schema hash
     */
    private readonly hashStorageKey: string;
    /**
     * A validation function to check whether the cached schema is still usable
     */
    private readonly isCacheUsable: (schema: Schema) => Promise<boolean>;
    /**
     * A processing function to be applied on a schema that has been retrieved and is going to be stored in cache
     */
    private readonly onRetrieved: (schema: Schema) => Promise<Schema>;

    /**
     * Initializes this loader
     * @param inner The inner loader
     * @param storageKey Initializes this loader
     * @param isCacheUsable A validation function to check whether the cached schema is still usable
     * @param onRetrieved A processing function to be applied on a schema that has been retrieved and is going to be stored in cache
     */
    /**
     * Promise used to fetch the most recent schema hash from the server
     */
    public readonly getSchemaHash: () => Promise<string | null>;
    constructor(
        inner: EntitySchemasLoader,
        storageKey: string,
        hashStorageKey: string | null = null,
        isCacheUsable: (schema: Schema) => Promise<boolean> = (_) => {
            return inner.getSchemaHash().then((hashStr: string) => {
                const oldHash = window.localStorage.getItem(
                    this.hashStorageKey
                );
                return oldHash === hashStr;
            });
        },
        onRetrieved: (schema: Schema) => Promise<Schema> = (schema) =>
            new Promise((resolve, _) => resolve(schema))
    ) {
        this.inner = inner;
        this.storageKey = storageKey;
        this.hashStorageKey = hashStorageKey
            ? hashStorageKey
            : `${storageKey}_hash`;
        this.isCacheUsable = isCacheUsable;
        this.onRetrieved = onRetrieved;
        this.getSchemaHash = () => inner.getSchemaHash();
    }

    public load(): Promise<Schema> {
        if (window === undefined) {
            return this.loadFromInner();
        }
        const content = window.localStorage.getItem(this.storageKey);
        if (content === null || content === '') {
            // no cache => load from the inner loader
            return this.loadFromInner();
        }
        const schema: Schema = JSON.parse(content);
        return this.isCacheUsable(schema).then((usable) => {
            if (usable) {
                return new Promise((resolve, _) => resolve(schema));
            } else {
                // cache is not usable, reload from inner
                return this.loadFromInner();
            }
        });
    }

    /**
     * Loads from the inner loader
     */
    private loadFromInner(): Promise<Schema> {
        return Promise.all([
            this.inner.load().then(this.onRetrieved),
            this.inner.getSchemaHash(),
        ]).then(([schema, hash]) => {
            if (window !== undefined && hash !== null) {
                window.localStorage.setItem(
                    this.storageKey,
                    JSON.stringify(schema)
                );
                window.localStorage.setItem(this.hashStorageKey, hash);
            }
            return schema;
        });
    }
}

/**
 * A loader that performs RQL queries to retrieve the schemas
 */
export class RqlEntitySchemasLoader implements EntitySchemasLoader {
    /**
     * The RQL client to use
     */
    public readonly rqlClient: RqlClient;

    constructor(rqlClient: RqlClient) {
        this.rqlClient = rqlClient;
    }

    /**
     * Loads all entity schemas
     */
    public load(): Promise<Schema> {
        return this.rqlClient.getSchema();
    }

    public getSchemaHash(): Promise<string | null> {
        return this.rqlClient.getSchemaHash();
    }
}

/**
 * The base implementation of a provider of entity schema that requires its content to be loaded
 */
export class LoadableEntitySchemaProvider implements EntitySchemaProvider {
    /**
     * The loader to use
     */
    private readonly loader: EntitySchemasLoader;
    /**
     * The promise the promise the provider is currently loading with
     */
    private isLoadingWith: Promise<Schema> | null;
    /**
     * Whether the provider has loaded data and is ready
     */
    private isLoaded: boolean;
    /**
     * The entity schemas by name
     */
    protected schemaByName: {[name: string]: EntitySchema | undefined};
    /**
     * The entity schemas by id
     */
    protected schemaById: {[eid: number]: EntitySchema | undefined};

    constructor(loader: EntitySchemasLoader) {
        this.loader = loader;
        this.schemaByName = {};
        this.schemaById = {};
        this.isLoadingWith = null;
        this.isLoaded = false;
    }

    /**
     * Loads the content of the
     */
    public load(): Promise<Schema> {
        if (this.isLoaded) {
            // FIXME This shouldn't return an empty schema,
            // the logic is a little convoluted
            return new Promise((resolve, _) =>
                resolve({entities: [], metadata: {}})
            );
        }
        if (this.isLoadingWith !== null) {
            let newPromise = this.isLoadingWith.then((data) => data);
            this.isLoadingWith = newPromise;
            return newPromise;
        }
        this.isLoadingWith = this.loader.load().then((schema) => {
            for (const entitySchema of schema.entities) {
                this.schemaById[entitySchema.eid] = entitySchema;
                this.schemaByName[entitySchema.name] = entitySchema;
            }
            this.isLoadingWith = null;
            this.isLoaded = true;
            return schema;
        });
        return this.isLoadingWith;
    }

    private closeRelations(
        typeEid: number,
        relationsFrom: EntityRelationSchema[],
        relationsTo: EntityRelationSchema[]
    ) {
        const typeSchema = this.schemaById[typeEid];
        if (typeSchema === undefined) {
            return;
        }
        relationsFrom.push(...typeSchema.relationsFrom);
        relationsTo.push(...typeSchema.relationsTo);
        if (typeSchema.specializes !== null) {
            this.closeRelations(
                typeSchema.specializes,
                relationsFrom,
                relationsTo
            );
        }
    }

    public getAllRelationsFor(typeEid: number): {
        relationsTo: EntityRelationSchema[];
        relationsFrom: EntityRelationSchema[];
    } {
        const relationsFrom: EntityRelationSchema[] = [];
        const relationsTo: EntityRelationSchema[] = [];
        this.closeRelations(typeEid, relationsFrom, relationsTo);
        return {
            relationsTo,
            relationsFrom,
        };
    }

    public getEntitySchemaByName(
        typeName: string
    ): Promise<EntitySchema | null> {
        return this.load().then((_) =>
            undefinedToNull(this.schemaByName[typeName])
        );
    }

    public getEntitySchemaById(typeEid: number): Promise<EntitySchema | null> {
        return this.load().then((_) =>
            undefinedToNull(this.schemaById[typeEid])
        );
    }

    public getEntitySchemaFor(eid: number): Promise<EntitySchema | null> {
        throw new Error('not implemented');
    }
}

/**
 * A schema provider that relies on an RQL interface
 */
export class RqlEntitySchemaProvider extends LoadableEntitySchemaProvider {
    /**
     * The RQL client to use
     */
    public readonly rqlClient: RqlClient;

    /**
     * Initializes this provider
     * @param rqlClient The RQL client to use
     * @param storageKey The storage key to the browser's local storage if the schemas are to be cached in the browser's local storage
     * @param isCacheUsable A validation function to check whether the cached schema is still usable
     * @param onRetrieved A processing function to be applied on a schema that has been retrieved and is going to be stored in cache
     */
    constructor(
        rqlClient: RqlClient,
        storageKey?: string,
        hashStorageKey: string | null = null,
        isCacheUsable: (schema: Schema) => Promise<boolean> = (_) =>
            new Promise((resolve, _) => resolve(true)),
        onRetrieved: (schema: Schema) => Promise<Schema> = (schema) =>
            new Promise((resolve, _) => resolve(schema))
    ) {
        super(
            storageKey === undefined
                ? new RqlEntitySchemasLoader(rqlClient)
                : new LocalStorageEntitySchemasLoader(
                      new RqlEntitySchemasLoader(rqlClient),
                      storageKey,
                      hashStorageKey || null
                  )
        );
        this.rqlClient = rqlClient;
    }

    public getEntitySchemaFor(eid: number): Promise<EntitySchema | null> {
        return this.load().then((_) => {
            return this.rqlClient
                .queryRows('Any TYPE WHERE X eid %(eid)s, X is TYPE', {
                    eid: eid,
                })
                .then((rows) => {
                    if (rows.length === 0) {
                        return null;
                    }
                    return undefinedToNull(this.schemaById[rows[0][0]]);
                });
        });
    }

    public getSchemaHash(): Promise<string | null> {
        return this.rqlClient.getSchemaHash();
    }
}

/**
 * An entity in Cubicweb
 */
export interface Entity {
    [key: string]: any;
}

/**
 * Provide access to entities ans their schema
 */
export interface EntityProvider {
    /**
     * Gets a specific entity
     * @param eid The EID of an entity (instance)
     */
    getEntity(eid: number): Promise<Entity | null>;
    /**
     * Gets a specific entity
     * @param eid The EID of an entity (instance)
     * @param schema The expected schema of the entity
     */
    getEntityGivenSchema(
        eid: number,
        schema: EntitySchema
    ): Promise<Entity | null>;
    /**
     * Gets the title for an entity
     * @param eid The EID of an entity (instance)
     * @param schema The expected schema of the entity
     */
    getEntityTitleGivenSchema(
        eid: number,
        schema: EntitySchema
    ): Promise<string | null>;
}

/**
 * Provides access to entities through a RQL enpoint
 */
export class RqlEntityProvider implements EntityProvider {
    /**
     * The RQL enpoint
     */
    public readonly rqlClient: RqlClient;
    /**
     * The associated provider of entity schemas
     */
    public readonly schemaProvider: EntitySchemaProvider;

    constructor(rqlClient: RqlClient, schemaProvider?: EntitySchemaProvider) {
        this.rqlClient = rqlClient;
        if (schemaProvider === undefined) {
            this.schemaProvider = new RqlEntitySchemaProvider(rqlClient);
        } else {
            this.schemaProvider = schemaProvider;
        }
    }

    public getEntity(eid: number): Promise<Entity | null> {
        return this.schemaProvider.getEntitySchemaFor(eid).then((schema) => {
            if (schema === null) {
                return null;
            }
            return this.getEntityGivenSchema(eid, schema);
        });
    }

    public getEntityGivenSchema(
        eid: number,
        schema: EntitySchema
    ): Promise<Entity | null> {
        let queries: RqlQuery[] = [];
        // query for attributes
        queries.push([
            'Any ' +
                schema.attributes.map((_, index) => `X${index}`).join(',') +
                ' WHERE X eid %(eid)s, ' +
                schema.attributes
                    .map((attribute, index) => `X ${attribute.name} X${index}`)
                    .join(','),
            {eid: eid},
        ]);
        let {relationsTo, relationsFrom} =
            this.schemaProvider.getAllRelationsFor(schema.eid);
        // queries for relations (as a subject)
        relationsTo.forEach((relation: EntityRelationSchema) => {
            const statement = `Any Y WHERE X eid %(eidSubject)s, X ${relation.name} Y, Y is T, T eid %(eidObject)s`;
            queries.push([
                statement,
                {eidSubject: eid, eidObject: relation.to},
            ]);
        });
        // queries for relations (as a object)
        relationsFrom.forEach((relation: EntityRelationSchema) => {
            const statement = `Any Y WHERE X eid %(eidObject)s, Y ${relation.name} X, Y is T, T eid %(eidSubject)s`;
            queries.push([
                statement,
                {eidObject: eid, eidSubject: relation.from},
            ]);
        });
        // run the queries in a transactionV1
        return this.rqlClient.transactionV1(queries).then((results) => {
            if (results[0].length === 0) {
                // no data for the first query
                return null;
            }
            let result: Entity = {'@type': schema.name, '@eid': eid};
            schema.attributes.forEach((attribute, index) => {
                result[attribute.name] = results[0][0][index];
            });
            let resultsIndex = 1;
            relationsTo.forEach((relation: EntityRelationSchema) => {
                const relationName = getUniqueRelationName(relation);
                const rows = results[resultsIndex];
                const values = rows.map((row: RqlRow) => row[0]);
                if (
                    Object.getOwnPropertyNames(result).indexOf(relationName) >=
                    0
                ) {
                    result[relationName] = result[relationName].concat(values);
                } else {
                    result[relationName] = values;
                }
                resultsIndex += 1;
            });
            relationsFrom.forEach((relation: EntityRelationSchema) => {
                let relationName = getUniqueRelationName(relation);
                const rows = results[resultsIndex];
                let values = rows.map((row: RqlRow) => row[0]);
                if (
                    Object.getOwnPropertyNames(result).indexOf(relationName) >=
                    0
                ) {
                    result[relationName] = result[relationName].concat(values);
                } else {
                    result[relationName] = values;
                }
                resultsIndex += 1;
            });
            return result;
        });
    }

    public getEntityTitleGivenSchema(
        eid: number,
        schema: EntitySchema
    ): Promise<string | null> {
        return this.rqlClient
            .queryRows(
                `Any Y WHERE X eid %(eid)s, X ${schema.attributes[0].name} Y`,
                {
                    eid: eid,
                }
            )
            .then((rows) => {
                if (rows.length === 0) {
                    return null;
                }
                let value = rows[0][0];
                return value.toString();
            });
    }
}

/**
 * Provides access to entities through the ejsonexport view of a RQL endpoint
 */
export class EJsonExportEntityProvider implements EntityProvider {
    /**
     * The RQL enpoint
     */
    public readonly rqlClient: RqlClient;

    constructor(rqlClient: RqlClient) {
        this.rqlClient = rqlClient;
    }

    public getEntity(eid: number): Promise<Entity | null> {
        return this.rqlClient
            .queryAndTransform(
                'Any X WHERE X eid %(eid)s',
                {eid: eid},
                'ejsonexport'
            )
            .then((entities) => {
                entities[0]['@type'] = entities[0]['cw_etype'];
                entities[0]['@eid'] = eid;
                return entities[0];
            });
    }

    public getEntityGivenSchema(
        eid: number,
        schema: EntitySchema
    ): Promise<Entity | null> {
        return this.getEntity(eid);
    }

    public getEntityTitleGivenSchema(
        eid: number,
        schema: EntitySchema
    ): Promise<string | null> {
        return this.getEntity(eid).then((entity) => {
            if (entity === null) {
                return null;
            }
            return getTitleFor(entity, schema);
        });
    }
}

/**
 * Caches the access to entities provided by another provider
 */
export class CachedEntityProvider implements EntityProvider {
    /**
     * The inner entity provider
     */
    public readonly inner: EntityProvider;
    /**
     * The cache
     */
    private cache: {[eid: number]: Entity | undefined};

    constructor(inner: EntityProvider) {
        this.inner = inner;
        this.cache = {};
    }

    /**
     * Evicts an entity from the cache
     * @param eid The eid of the entity
     */
    public evict(eid: number) {
        delete this.cache[eid];
    }

    /**
     * Clears the entire cache
     */
    public clear() {
        this.cache = {};
    }

    public getEntity(eid: number): Promise<Entity | null> {
        let entity = this.cache[eid];
        if (entity !== undefined) {
            return new Promise((resolve, _) => resolve(entity));
        }
        return this.inner.getEntity(eid).then((entity) => {
            if (entity === null) {
                return null;
            }
            this.cache[eid] = entity;
            return entity;
        });
    }

    public getEntityGivenSchema(
        eid: number,
        schema: EntitySchema
    ): Promise<Entity | null> {
        let entity = this.cache[eid];
        if (entity !== undefined) {
            return new Promise((resolve, _) => resolve(entity));
        }
        return this.inner.getEntityGivenSchema(eid, schema).then((entity) => {
            if (entity === null) {
                return null;
            }
            this.cache[eid] = entity;
            return entity;
        });
    }

    public getEntityTitleGivenSchema(
        eid: number,
        schema: EntitySchema
    ): Promise<string | null> {
        let entity = this.cache[eid];
        if (entity !== undefined) {
            return new Promise((resolve, _) =>
                getTitleFor(entity as Entity, schema)
            );
        }
        return this.inner.getEntityTitleGivenSchema(eid, schema);
    }
}

/**
 * Gets the title of an entity
 * @param entity The entity
 * @param schema Its schema
 */
export function getTitleFor(entity: Entity, schema: EntitySchema): string {
    let value = entity[schema.attributes[0].name];
    if (value === undefined || value === null) {
        return '';
    }
    return value.toString();
}
